import { __decorate } from "tslib";
import '../form-msg/element';
import '../../../atom/label/element';
import { CheckedElement } from '../../../../lib/validate/checkedElement';
import '../../../atom/radio/element';
import { CheckErrorMsg } from '../../../../lib/validate/checkErrorMsg';
import { css, LitElement, html } from 'lit';
import { property, query, queryAssignedElements } from 'lit/decorators.js';
export class FormRadio extends LitElement {
    constructor() {
        super(...arguments);
        this.validator = new CheckedElement(this);
        this.value = '';
        this.disabled = false;
        this.requiredInfoText = '* This field is required';
        this.slotChangeEventListener = (e) => {
            e.stopImmediatePropagation();
            e.stopPropagation();
            this._updateItems();
        };
        this.changeEventListener = (e) => {
            e.stopImmediatePropagation();
            e.stopPropagation();
            this.change(e);
        };
        this.topLevelLabelClick = (e) => {
            e.stopImmediatePropagation();
            e.stopPropagation();
            const ce = new CustomEvent('clickTopLevelLabel', {
                bubbles: true,
                composed: true,
                detail: {
                    target: e.target,
                    parent: this
                }
            });
            e.target.dispatchEvent(ce);
        };
    }
    render() {
        return html `
            <ep-label style="display: none;" class="top-level-label" @click="${this.topLevelLabelClick}"></ep-label>
            <div>
                <slot></slot>
            </div>
            <ep-form-msg></ep-form-msg>
        `;
    }
    firstUpdated() {
        this.slotElements = this._slot.assignedNodes();
        this._slot.addEventListener('slotchange', this.slotChangeEventListener);
        setTimeout(() => {
            this.defineFor();
        }, 50);
    }
    updated(_changedProperties) {
        super.updated(_changedProperties);
        if (this.label !== undefined && this.label.trim() !== '') {
            this._label.style.setProperty('display', 'block');
            this._label.style.setProperty('cursor', 'default');
            this._label.innerText = this.label;
        }
        if (this.infoText !== undefined && !this.hasAttribute('required')) {
            this._formMsg.infoText = this.infoText;
        }
        if (this.hasAttribute('required')) {
            this.watchRequired();
        }
        if (this.hasAttribute('disabled'))
            this.setAttribute('disabled', '');
    }
    disconnectedCallback() {
        if (this._slot)
            this._slot.removeEventListener('slotchange', this.slotChangeEventListener);
        this._radios.forEach((radio) => {
            radio.removeEventListener('change', this.changeEventListener);
        });
    }
    removeChecked(value) {
        this.removeAttribute('value');
        this._radios.forEach((radio) => {
            if (radio._input.value !== value) {
                radio.removeAttribute('checked');
                radio._input.checked = false;
            }
        });
    }
    _updateItems() {
        this._slot.assignedElements().forEach((item) => {
            item.addEventListener('change', this.changeEventListener);
        });
    }
    defineFor() {
        this.setAttribute('name', this.name);
        for (let i = 0; i < this._radios.length; i++) {
            if (!this._radios[i].name)
                this._radios[i].setAttribute('name', this.name + [i]);
        }
    }
    watchRequired() {
        var _a;
        this.setAttribute('required', '');
        if (!((_a = this.infoText) === null || _a === void 0 ? void 0 : _a.includes(`${this.requiredInfoText}`)) && this.infoText !== undefined) {
            this.infoText = `${this.requiredInfoText} <br/> ${this.infoText}`;
            this._formMsg.infoText = this.infoText;
        }
        if (this.infoText === undefined) {
            this.infoText = `${this.requiredInfoText}`;
            this._formMsg.infoText = this.infoText;
        }
    }
    change(e) {
        e.stopImmediatePropagation();
        e.stopPropagation();
        let value = '';
        const evt = e;
        for (let i = 0; i < this._radios.length; i++) {
            const input = this._radios[i].shadowRoot.querySelector('input');
            this.removeChecked(evt.detail);
            if (input.checked) {
                this._radios[i].setAttribute('checked', '');
                this.setAttribute('value', input.value);
                value = input.value;
            }
            if (!input.checked)
                this._radios[i].removeAttribute('checked');
        }
        this.value = value !== '' ? value : '';
        new CheckErrorMsg(this).checkErrorMsg(this.validator.getValidator());
        this.dispatchEvent(new CustomEvent('change', {
            composed: true,
            detail: this.value
        }));
    }
}
FormRadio.styles = css `
        :host {
            display: block;
        }

        ep-label {
            cursor: pointer;
            margin-bottom: 10px;
        }

        ep-label.top-level-label {
            --ep-label-text-font-size: var(--ep-form-radio-top-label-font-size, --ep-text-font-size);
            float: var(--ep-form-radio-top-level-float);
            width: fit-content;
        }

        ep-radio,
        ep-checkbox {
            margin-right: var(--ep-form-radio-element-margin-right, 20px);
        }

        div {
            display: var(--ep-radio-display, grid);
            grid-template-columns: var(--ep-radio-grid-columns, 1fr 1fr 1fr 1fr);
            grid-gap: var(--ep-radio-grid-gap, 16px);
            grid-row-gap: var(--ep-radio-grid-column, 16px);
            margin: var(--ep-form-radio-span-margin, auto);
        }

        :host([required]) ep-label.top-level-label:after {
            content: '*';
            font-size: 12px;
            vertical-align: top;
            color: var(--ep-label-text-color);
        }

        :host([noAfter]) ep-label.top-level-label:after {
            display: none;
        }

        @media only screen and (max-width: 500px) {
            grid-template-columns: var(--ep-radio-grid-columns-sm, 1fr);
        }

        @media only screen and (min-width: 500px) and (max-width: 800px) {
            grid-template-columns: var(--ep-radio-grid-columns-md, 1fr 1fr);
        }
    `;
__decorate([
    property()
], FormRadio.prototype, "name", void 0);
__decorate([
    property()
], FormRadio.prototype, "value", void 0);
__decorate([
    property({ type: Boolean })
], FormRadio.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], FormRadio.prototype, "required", void 0);
__decorate([
    property()
], FormRadio.prototype, "requiredErrorMsg", void 0);
__decorate([
    property()
], FormRadio.prototype, "requiredInfoText", void 0);
__decorate([
    property()
], FormRadio.prototype, "infoText", void 0);
__decorate([
    property()
], FormRadio.prototype, "label", void 0);
__decorate([
    query('ep-label')
], FormRadio.prototype, "_label", void 0);
__decorate([
    query('slot')
], FormRadio.prototype, "_slot", void 0);
__decorate([
    query('ep-form-msg')
], FormRadio.prototype, "_formMsg", void 0);
__decorate([
    query('span')
], FormRadio.prototype, "_span", void 0);
__decorate([
    queryAssignedElements()
], FormRadio.prototype, "_radios", void 0);
if (!customElements.get('ep-form-radio')) {
    customElements.define('ep-form-radio', FormRadio);
}
//# sourceMappingURL=element.js.map